import { useEffect, useRef } from 'react';
import { API, useDevices } from '@unboared/react';

import {
    CastVotePayload,
    FP_ACTION_CAST_VOTE,
    FP_ACTION_REQUEST_CHANGE,
    FP_ACTION_REQUEST_START,
    FP_STATE_KEY,
    FreePlayState,
    RequestChangePayload,
    RequestStartPayload,
} from '~/services/freePlay/types';
import { VoteService } from '~/services/freePlay/VoteService';

import { FREE_PLAY_ABANDON_MS, useFreePlayPresence } from './useFreePlayPresence';

const TICK_INTERVAL_MS = 500;
/** Once every gamepad is gone past the abandon grace, wait this much longer before
    resetting to idle. Short, because FREE_PLAY_ABANDON_MS (90s) already absorbs a lone
    player stepping away — this only guards against the host's OWN getGamepadIDs()
    blinking empty during its socket reconnection churn. */
const RESET_CONFIRM_MS = 3000;

type Options = {
    enabled: boolean;
    tickIntervalMs?: number;
};

export function useFreePlayHostOrchestrator({ enabled, tickIntervalMs = TICK_INTERVAL_MS }: Options) {
    const { hostID, deviceID } = useDevices();
    const isHost = !!hostID && deviceID === hostID;

    // Effective presence: away-aware view of the gamepads (see useFreePlayPresence).
    // getGamepadIDs() keeps a device until the server grace expires (minutes/hours),
    // so the raw count never drops when a player closes their tab — this layers the
    // lib's real-time away/back events on top so the orchestrator reacts in ~15s.
    const { getPresentGamepadIDs } = useFreePlayPresence(enabled && isHost);
    const getPresentRef = useRef(getPresentGamepadIDs);
    getPresentRef.current = getPresentGamepadIDs;

    useEffect(() => {
        if (!enabled || !isHost || !hostID) return undefined;

        const readState = (): FreePlayState => {
            const stored = API.getSharedProperty(hostID, FP_STATE_KEY) as FreePlayState | undefined;
            return stored ?? VoteService.initial();
        };

        const writeState = (next: FreePlayState) => {
            API.loadSharedProperty(hostID, FP_STATE_KEY, next);
        };

        if (!API.getSharedProperty(hostID, FP_STATE_KEY)) {
            writeState(VoteService.initial());
        }

        const offStart = API.onMessage(FP_ACTION_REQUEST_START, (_from, data: RequestStartPayload) => {
            if (!data?.gameID) return;
            const state = readState();
            if (state.phase !== 'idle') return;
            writeState(VoteService.startGame(state, data.gameID));
        });

        const offChange = API.onMessage(FP_ACTION_REQUEST_CHANGE, (from, data: RequestChangePayload) => {
            if (!data?.gameID) return;
            const state = readState();
            /* Voter pool: prefer the game-declared active players; fall back to the
               present gamepads, then to the initiator alone. Some games don't push
               their active list back to the parent — without this fallback, a lone
               player could never propose a change. */
            let voters = API.getActivePlayers();
            if (!voters || voters.length === 0) voters = getPresentRef.current();
            if (!voters || voters.length === 0) voters = [from];
            // The proposer must always be able to vote on their own proposal. Some games
            // report an active-players list that excludes a player still in their lobby —
            // without this, `from` would be left out of eligibleVoters and become a
            // spectator on the very change they requested.
            if (!voters.includes(from)) voters = [...voters, from];
            const attempt = VoteService.startVote(state, {
                initiatorID: from,
                proposedGameID: data.gameID,
                activePlayers: voters,
                now: Date.now(),
            });
            if (!attempt.ok) {
                /* eslint-disable-next-line no-console */
                console.warn('[free-play] startVote rejected', attempt.reason, {
                    from,
                    proposedGameID: data.gameID,
                    activePlayers: voters,
                });
                return;
            }
            writeState(attempt.state);
        });

        const offVote = API.onMessage(FP_ACTION_CAST_VOTE, (from, data: CastVotePayload) => {
            if (!data?.vote) return;
            const state = readState();
            const after = VoteService.castVote(state, from, data.vote);
            const settled = VoteService.settle(after, Date.now());
            writeState(settled.state);
        });

        /* Tracks since when the session has had zero present gamepads, to confirm the
           reset across a few ticks (see RESET_CONFIRM_MS). */
        let presentEmptySince: number | null = null;
        /* Same idea, scoped to a vote whose pool emptied with nobody left in the room:
           confirm the empty room briefly before resetting to idle (no zombie game). */
        let voteEmptyRoomSince: number | null = null;

        const tick = window.setInterval(() => {
            const state = readState();
            const now = Date.now();

            // RESET to idle (destructive: tears down the running game) uses the long
            // abandon grace so a lone player who stepped away — e.g. to answer a text —
            // can come back to their game. Only when EVERY gamepad has been gone that
            // long do we return to the QR attract screen for the next passenger.
            if (state.phase !== 'idle') {
                const stillHere = getPresentRef.current(FREE_PLAY_ABANDON_MS);
                if (stillHere.length === 0) {
                    if (presentEmptySince === null) presentEmptySince = now;
                    else if (now - presentEmptySince >= RESET_CONFIRM_MS) {
                        presentEmptySince = null;
                        writeState(VoteService.endGame(state));
                        return;
                    }
                } else {
                    presentEmptySince = null;
                }
            } else {
                presentEmptySince = null;
            }

            // Vote lifecycle. Uses the SHORT grace: a player away a few seconds shouldn't
            // block a live vote (reversible — they're re-counted instantly on return).
            if (state.phase !== 'voting' || !state.vote) {
                voteEmptyRoomSince = null;
                return;
            }
            const present = getPresentRef.current();
            /* Prune voters who effectively left mid-vote so the quorum reflects who is
               actually still here (decision 2026-06-18); the presence grace already
               protects against transient blips, so a missing voter here is a real one.
               If the pool empties out entirely, abandon the proposal. */
            const reconciled = VoteService.reconcileVoters(state, present);
            if (reconciled.vote && reconciled.vote.eligibleVoters.length === 0) {
                if (present.length > 0) {
                    /* Spectators remain → drop the proposal and let them keep playing. */
                    voteEmptyRoomSince = null;
                    writeState(VoteService.abandonVote(reconciled));
                    return;
                }
                /* Room is empty → reset straight to idle rather than resuming a zombie
                   game for the 90s abandon grace (nobody is "stepping away", the voters
                   left). Stay in 'voting' across a short confirm window so a transient
                   empty getGamepadIDs() during host socket churn can't reset on a blink. */
                if (voteEmptyRoomSince === null) voteEmptyRoomSince = now;
                if (now - voteEmptyRoomSince >= RESET_CONFIRM_MS) {
                    voteEmptyRoomSince = null;
                    writeState(VoteService.endGame(state));
                } else if (reconciled !== state) {
                    writeState(reconciled);
                }
                return;
            }
            voteEmptyRoomSince = null;
            const settled = VoteService.settle(reconciled, now);
            if (settled.result !== 'pending') {
                writeState(settled.state);
                return;
            }
            /* No verdict yet, but persist the pruned voter pool so every device sees
               the updated quorum. reconcileVoters returns the same ref when unchanged. */
            if (reconciled !== state) writeState(reconciled);
        }, tickIntervalMs);

        return () => {
            offStart();
            offChange();
            offVote();
            window.clearInterval(tick);
        };
    }, [enabled, isHost, hostID, tickIntervalMs]);
}
